import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Search, Plus, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, AlertTriangle, DollarSign,
  Building, FileText, Send, Eye, Ban, Play
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API_URL } from '../../utils/apiConfig';

const StatusBadge = ({ status }) => {
  const colors = {
    draft: 'bg-slate-600 text-slate-200',
    pending_review: 'bg-amber-600 text-amber-100',
    pending_approval: 'bg-orange-600 text-orange-100',
    approved: 'bg-blue-600 text-blue-100',
    processing: 'bg-purple-600 text-purple-100',
    completed: 'bg-emerald-600 text-emerald-100',
    failed: 'bg-red-600 text-red-100',
    cancelled: 'bg-slate-500 text-slate-200',
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${colors[status] || 'bg-slate-600 text-slate-200'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

export default function AdminSettlementsView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [creating, setCreating] = useState(false);
  const [createMerchantId, setCreateMerchantId] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('pecash_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settlements/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, []);

  const fetchSettlements = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/settlements?limit=100`;
      if (selectedStatus) url += `&status=${selectedStatus}`;
      if (selectedMerchant) url += `&merchant_id=${selectedMerchant}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettlements(data.settlements || []);
      }
    } catch (err) {
      console.error('Settlements fetch error:', err);
    }
  }, [selectedStatus, selectedMerchant]);

  const fetchMerchants = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settlements/merchants?min_balance=0`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMerchants(data.merchants || []);
      }
    } catch (err) {
      console.error('Merchants fetch error:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchSettlements(), fetchMerchants()]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchSettlements, fetchMerchants]);

  useEffect(() => {
    fetchSettlements();
  }, [selectedStatus, selectedMerchant, fetchSettlements]);

  const handleCreateSettlement = async () => {
    if (!createMerchantId) return;
    setCreating(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/admin/settlements/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ merchant_id: createMerchantId })
      });

      const data = await res.json();
      if (res.ok) {
        setCreateMerchantId('');
        await Promise.all([fetchDashboard(), fetchSettlements(), fetchMerchants()]);
        setActiveTab('settlements');
      } else {
        setError(data.detail || 'Failed to create settlement');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleAction = async (settlementId, action, notes = '') => {
    setActionLoading(settlementId);
    setError('');

    try {
      let endpoint = '';
      let method = 'POST';
      let body = {};

      switch (action) {
        case 'submit':
          endpoint = `/api/admin/settlements/${settlementId}/submit`;
          break;
        case 'review_approve':
          endpoint = `/api/admin/settlements/${settlementId}/review`;
          body = { action: 'approve', notes };
          break;
        case 'review_reject':
          endpoint = `/api/admin/settlements/${settlementId}/review`;
          body = { action: 'reject', notes };
          break;
        case 'approve':
          endpoint = `/api/admin/settlements/${settlementId}/approve`;
          body = { action: 'approve', notes };
          break;
        case 'reject':
          endpoint = `/api/admin/settlements/${settlementId}/approve`;
          body = { action: 'reject', notes };
          break;
        case 'process':
          endpoint = `/api/admin/settlements/${settlementId}/process`;
          break;
        case 'cancel':
          endpoint = `/api/admin/settlements/${settlementId}/cancel?reason=Admin cancelled`;
          break;
        default:
          return;
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
      });

      if (res.ok) {
        await Promise.all([fetchDashboard(), fetchSettlements()]);
      } else {
        const data = await res.json();
        setError(data.detail || 'Action failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  const getActionButtons = (settlement) => {
    const s = settlement.status;
    const id = settlement.id;
    const isLoading = actionLoading === id;

    const btnClass = "text-xs px-2 py-1 rounded";

    if (s === 'draft') {
      return (
        <div className="flex gap-2">
          <Button 
            size="sm" 
            className={`${btnClass} bg-blue-600 hover:bg-blue-700`}
            onClick={() => handleAction(id, 'submit')}
            disabled={isLoading}
          >
            <Send className="w-3 h-3 mr-1" /> Submit
          </Button>
          <Button 
            size="sm" 
            variant="destructive"
            className={btnClass}
            onClick={() => handleAction(id, 'cancel')}
            disabled={isLoading}
          >
            <Ban className="w-3 h-3 mr-1" /> Cancel
          </Button>
        </div>
      );
    }

    if (s === 'pending_review') {
      return (
        <div className="flex gap-2">
          <Button 
            size="sm" 
            className={`${btnClass} bg-emerald-600 hover:bg-emerald-700`}
            onClick={() => handleAction(id, 'review_approve')}
            disabled={isLoading}
          >
            <CheckCircle className="w-3 h-3 mr-1" /> Approve Review
          </Button>
          <Button 
            size="sm" 
            variant="destructive"
            className={btnClass}
            onClick={() => handleAction(id, 'review_reject')}
            disabled={isLoading}
          >
            <XCircle className="w-3 h-3 mr-1" /> Reject
          </Button>
        </div>
      );
    }

    if (s === 'pending_approval') {
      return (
        <div className="flex gap-2">
          <Button 
            size="sm" 
            className={`${btnClass} bg-emerald-600 hover:bg-emerald-700`}
            onClick={() => handleAction(id, 'approve')}
            disabled={isLoading}
          >
            <CheckCircle className="w-3 h-3 mr-1" /> Final Approve
          </Button>
          <Button 
            size="sm" 
            variant="destructive"
            className={btnClass}
            onClick={() => handleAction(id, 'reject')}
            disabled={isLoading}
          >
            <XCircle className="w-3 h-3 mr-1" /> Reject
          </Button>
        </div>
      );
    }

    if (s === 'approved') {
      return (
        <Button 
          size="sm" 
          className={`${btnClass} bg-purple-600 hover:bg-purple-700`}
          onClick={() => handleAction(id, 'process')}
          disabled={isLoading}
        >
          <Play className="w-3 h-3 mr-1" /> Process Payout
        </Button>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Building className="w-6 h-6 text-emerald-400" />
                Merchant Settlements
              </h1>
              <p className="text-sm text-slate-400">Manage payout batches to colmados</p>
            </div>
          </div>
          {dashboard?.pilot_mode && (
            <span className="px-3 py-1 bg-amber-900/50 border border-amber-700 rounded-full text-amber-300 text-xs font-medium">
              PILOT MODE
            </span>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-xl p-4"
            >
              <p className="text-slate-400 text-xs mb-1">Draft</p>
              <p className="text-2xl font-bold text-white">{dashboard.status_summary?.draft || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-amber-700/50 rounded-xl p-4"
            >
              <p className="text-amber-400 text-xs mb-1">Pending Review</p>
              <p className="text-2xl font-bold text-white">{dashboard.status_summary?.pending_review || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-orange-700/50 rounded-xl p-4"
            >
              <p className="text-orange-400 text-xs mb-1">Pending Approval</p>
              <p className="text-2xl font-bold text-white">{dashboard.status_summary?.pending_approval || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-blue-700/50 rounded-xl p-4"
            >
              <p className="text-blue-400 text-xs mb-1">Approved</p>
              <p className="text-2xl font-bold text-white">{dashboard.status_summary?.approved || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800 border border-emerald-700/50 rounded-xl p-4"
            >
              <p className="text-emerald-400 text-xs mb-1">Completed</p>
              <p className="text-2xl font-bold text-white">{dashboard.status_summary?.completed || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-slate-800 border border-purple-700/50 rounded-xl p-4"
            >
              <p className="text-purple-400 text-xs mb-1">Pending Value</p>
              <p className="text-2xl font-bold text-white">${(dashboard.pending_settlement_value_usd || 0).toLocaleString()}</p>
            </motion.div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          <Button
            data-testid="settlements-tab-dashboard"
            variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
            className={activeTab === 'dashboard' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </Button>
          <Button
            data-testid="settlements-tab-settlements"
            variant={activeTab === 'settlements' ? 'default' : 'ghost'}
            className={activeTab === 'settlements' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('settlements')}
          >
            Settlements ({settlements.length})
          </Button>
          <Button
            data-testid="settlements-tab-merchants"
            variant={activeTab === 'merchants' ? 'default' : 'ghost'}
            className={activeTab === 'merchants' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('merchants')}
          >
            Merchants ({merchants.length})
          </Button>
          <Button
            data-testid="settlements-tab-create"
            variant={activeTab === 'create' ? 'default' : 'ghost'}
            className={activeTab === 'create' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('create')}
          >
            <Plus className="w-4 h-4 mr-1" /> Create
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Recent Settlements</h3>
              {dashboard.recent_settlements?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent_settlements.map((s) => (
                    <div key={s.id} className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
                      <div>
                        <p className="text-white font-medium">{s.merchant_name || s.merchant_id}</p>
                        <p className="text-slate-400 text-sm">
                          ${s.gross_amount_usd?.toLocaleString()} gross / ${s.net_amount_usd?.toLocaleString()} net
                        </p>
                      </div>
                      <StatusBadge status={s.status} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No recent settlements</p>
              )}
            </div>
          </div>
        )}

        {/* Settlements Tab */}
        {activeTab === 'settlements' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 mb-4">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_review">Pending Review</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={selectedMerchant}
                onChange={(e) => setSelectedMerchant(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Merchants</option>
                {merchants.map((m) => (
                  <option key={m.merchant_id} value={m.merchant_id}>
                    {m.merchant_name || m.merchant_email}
                  </option>
                ))}
              </select>
            </div>

            {/* Settlements List */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Merchant</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Gross USD</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Fees</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Net USD</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Redemptions</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Status</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Created</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {settlements.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-slate-500 py-8">
                        No settlements found
                      </td>
                    </tr>
                  ) : (
                    settlements.map((s) => (
                      <React.Fragment key={s.id}>
                        <tr 
                          className="hover:bg-slate-700/50 cursor-pointer"
                          data-testid={`settlement-row-${s.id}`}
                          onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {expandedId === s.id ? (
                                <ChevronUp className="w-4 h-4 text-slate-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                              )}
                              <div>
                                <p className="text-white text-sm font-medium">{s.merchant_name || 'Unknown'}</p>
                                <p className="text-slate-500 text-xs">{s.merchant_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-white text-sm">${(s.gross_amount_usd || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-red-400 text-sm">-${(s.total_fees || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-emerald-400 text-sm font-medium">${(s.net_amount_usd || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-slate-400 text-sm">{s.redemption_count || 0}</td>
                          <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            {getActionButtons(s)}
                          </td>
                        </tr>
                        {expandedId === s.id && (
                          <tr className="bg-slate-900">
                            <td colSpan="8" className="px-4 py-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-slate-500">Settlement ID</p>
                                  <p className="text-white font-mono text-xs">{s.id}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Type</p>
                                  <p className="text-white capitalize">{s.settlement_type}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Period</p>
                                  <p className="text-white">{s.period_start?.slice(0, 10)} - {s.period_end?.slice(0, 10)}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Created By</p>
                                  <p className="text-white">{s.created_by}</p>
                                </div>
                                {s.reviewed_by && (
                                  <div>
                                    <p className="text-slate-500">Reviewed By</p>
                                    <p className="text-white">{s.reviewed_by}</p>
                                  </div>
                                )}
                                {s.approved_by && (
                                  <div>
                                    <p className="text-slate-500">Approved By</p>
                                    <p className="text-white">{s.approved_by}</p>
                                  </div>
                                )}
                                {s.payout_reference && (
                                  <div>
                                    <p className="text-slate-500">Payout Ref</p>
                                    <p className="text-emerald-400 font-mono">{s.payout_reference}</p>
                                  </div>
                                )}
                                {s.notes && (
                                  <div className="col-span-2">
                                    <p className="text-slate-500">Notes</p>
                                    <p className="text-white">{s.notes}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Merchants Tab */}
        {activeTab === 'merchants' && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-900 border-b border-slate-700">
                <tr>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Merchant</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Settlement Amount</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Pending Settlement</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Available</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Enforcement</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {merchants.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-slate-500 py-8">
                      No merchants with settlements
                    </td>
                  </tr>
                ) : (
                  merchants.map((m) => (
                    <tr key={m.merchant_id} className="hover:bg-slate-700/50">
                      <td className="px-4 py-3">
                        <p className="text-white text-sm font-medium">{m.merchant_name || 'Unknown'}</p>
                        <p className="text-slate-500 text-xs">{m.merchant_email}</p>
                      </td>
                      <td className="px-4 py-3 text-white text-sm">${(m.balance || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-amber-400 text-sm">${(m.pending_settlement || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-emerald-400 text-sm font-medium">${(m.available_for_settlement || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {m.enforcement_status && m.enforcement_status !== 'active' ? (
                          <span className="px-2 py-1 bg-red-900/50 text-red-400 rounded text-xs">
                            {m.enforcement_status}
                          </span>
                        ) : (
                          <span className="text-slate-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          disabled={m.available_for_settlement <= 0 || m.enforcement_status === 'blocked'}
                          className="text-xs bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => {
                            setCreateMerchantId(m.merchant_id);
                            setActiveTab('create');
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Create Settlement
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Tab */}
        {activeTab === 'create' && (
          <div className="max-w-xl mx-auto">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-400" />
                Create Settlement Batch
              </h3>
              <p className="text-slate-400 text-sm mb-6">
                This will gather all unsettled redemptions for the merchant and create a settlement batch in DRAFT status.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Select Merchant</label>
                  <select
                    value={createMerchantId}
                    onChange={(e) => setCreateMerchantId(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">-- Select a merchant --</option>
                    {merchants.filter(m => m.available_for_settlement > 0).map((m) => (
                      <option key={m.merchant_id} value={m.merchant_id}>
                        {m.merchant_name || m.merchant_email} (${m.available_for_settlement?.toFixed(2)} available)
                      </option>
                    ))}
                  </select>
                </div>

                <Button
                  onClick={handleCreateSettlement}
                  disabled={!createMerchantId || creating}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {creating ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Create Settlement Batch
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-700">
                <h4 className="text-white font-medium mb-2">Settlement Workflow</h4>
                <ol className="text-sm text-slate-400 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-600 text-xs flex items-center justify-center">1</span>
                    <span>Create → <strong className="text-white">DRAFT</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-600 text-xs flex items-center justify-center">2</span>
                    <span>Submit → <strong className="text-amber-400">PENDING REVIEW</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-600 text-xs flex items-center justify-center">3</span>
                    <span>Review Approve → <strong className="text-orange-400">PENDING APPROVAL</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-600 text-xs flex items-center justify-center">4</span>
                    <span>Final Approve → <strong className="text-blue-400">APPROVED</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-600 text-xs flex items-center justify-center">5</span>
                    <span>Process Payout → <strong className="text-emerald-400">COMPLETED</strong></span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
